import Phrase from 'Models/Phrase';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import { PhraseData } from 'Phaser/GameObjects/StaffLine';
import { PrerenderedPhraseData } from 'Types';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import { COLORBLIND_TYPE } from 'Utils/Constants';
import { ReactToPhaserEvent } from '.';
import { AtaConfig } from 'Phaser/Gameplay/AdaptiveTempoController';

/** App tour step: playback (cursor + metronome), or note-coloring steps */
export type AppTourStepPhaser =
  | 'playback'
  | 'perfect-notes'
  | 'off-time-notes'
  | 'error-notes'
  | 'auto-tempo-adjustment';

/**
 * OSMD phrase data type for the AddPhrase event when using OSMD backend.
 * OSMDPhraseData contains musicXml string instead of prerendered SVG/JSON.
 */
export interface OSMDPhraseDataEventPayload {
  phraseRaw: Phrase;
  phrase: {
    musicXml: string;
    uuid: string;
    startTimestamp: number;
    timeSignature: { numerator: number; denominator: number };
    showClef: boolean;
    showBrace: boolean;
    showTimeSig: boolean;
    transposeSemitones?: number;
  };
}

/**
 * Legacy SVG/JSON phrase data payload.
 */
interface LegacyPhraseDataEventPayload {
  phraseRaw: Phrase;
  phrase: PhraseData;
  cursorData: PrerenderedPhraseData[];
}

export type ToPhaserEvents = {
  ChangePlaybackRate: { rate: number };
  ChangeTempo: { tempo: number };
  UpdateObjective: { chapter: number; prompt?: string };
  PauseExercise: void;
  ResumeExercise: void;
  ToggleExercise: void;
  StartExercise: { tempo?: number; autoStart?: boolean };
  MidiOn: { note: number; velocity: number };
  MidiOff: { note: number };
  SkipExercise: void;
  UnloadExercise: void;
  AddPhraseLegacy: LegacyPhraseDataEventPayload;
  AddPhraseOSMD: OSMDPhraseDataEventPayload;
  AddAllOSMDPhrases: OSMDPhraseDataEventPayload[];
  PulseNote: { id: string; colorblindType?: COLORBLIND_TYPE | null; noteTimestamp?: number };
  SetNoteState: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
    noteTimestamp?: number;
  };
  SetCursor: { measure: number };
  ResizeWindow: { width: number; height: number; playButtonOverlap?: number };
  SetFreePlayMode: { enabled: boolean };
  SetZoomLevel: { zoomLevel: number };
  SoftRefreshRepertoire: void;
  SetCursorVisible: { visible: boolean };
  ResetChevrons: void;
  SetNoteColor: { color: string | number; id?: string };
  ResetNoteColor: { id?: string };
  SetAppTourStep: { step: AppTourStepPhaser; cbType: COLORBLIND_TYPE | null };
  UpdateGoalTempo: { goalTempo: number };
  EndTour: void;
  AbortAdaptiveTempo: { tempoRestorePoint: number };
  SetLoopingActive: { active: boolean };
  RestartLoopFromBeginning: void;
  SetAtaEnabled: { enabled: boolean };
  // MUS-243: User-intent events. React buttons emit these; the scene's
  // playback machine consumes them. Replaces the old pattern of dispatching
  // Redux play/pause state and having the container imperatively call
  // scheduler2 in response.
  UserPlay: void;
  UserPause: void;
  UserReset: void;
  UserToggleLoop: { active: boolean };
  AppBackgrounded: void;
  AppForegrounded: void;
  /** Container injects scheduler2 reference into the scene so the playback
   *  machine's actions can drive Scheduler2 directly without round-tripping
   *  through React events. */
  InjectScheduler2: { scheduler2: unknown };
  UpdateAtaConfg: { config: Partial<AtaConfig> };
  // A freshly fetched, untransformed phrase; the PhraseManager applies the
  // current transposition itself. `sequenceId` echoes the FetchPhrase request id
  // so the manager can reorder the initial fill back into request order.
  PhraseReady: { phrase: Phrase; sequenceId: number };
  // Live transpose / mode conversion change; the PhraseManager re-transposes the
  // on-screen phrases in place.
  SetSightReadingTranspose: { semitones: number; modeConversion: ParallelModeConversion };
};

/**
 * Create an AddAllOSMDPhrases event that sends all phrases in a single batch.
 * Used by RepertoirePlayContainerOSMD to avoid O(N²) per-phrase layout.
 */
export const AddAllOSMDPhrasesHelper = (
  phrasePayloads: Array<{ phrase: Phrase; musicXml: string }>,
  transposeSemitones: number = 0,
  showClef: boolean = true,
  showBrace: boolean = true,
): ReactToPhaserEvent => {
  const payloads: OSMDPhraseDataEventPayload[] = phrasePayloads.map(
    ({ phrase: phraseRaw, musicXml }, index) => ({
      phraseRaw,
      phrase: {
        musicXml,
        uuid: phraseRaw.uuid,
        startTimestamp: phraseRaw.getStartTimestamp(),
        timeSignature: {
          numerator: phraseRaw.musicXML.timeSignatures[0]?.numerator ?? 4,
          denominator: phraseRaw.musicXML.timeSignatures[0]?.denominator ?? 4,
        },
        showClef,
        showBrace,
        showTimeSig: index === 0,
        transposeSemitones,
      },
    }),
  );
  return ['AddAllOSMDPhrases', payloads];
};
